import React, {ReactNode, useEffect, useRef} from 'react'
import {Controller, Control, FieldErrors, FieldError} from 'react-hook-form'
import {useIntl} from 'react-intl'
import {Input, Textarea, Select, Radio, Checkbox} from '_uiComponents'
import {useGlobalConfigState} from '_context'

type FormValues = {
    name: string
    surname: string
    email: string
    'email-phone': string
    phone: string
    'select-test-1': string
    'select-test-2': string
    password: string
    message: string
    radio: string
    checkbox: string
    men: string
    women: string
    kids: string
    eyewear: string
    fragrances: string
    lifestyle: string
    preferenceNewsletterConsent: string
}

interface FormFactoryProps {
    control: Control<FormValues>
    type: string
    name: string
    label: string | ReactNode
    text?: string | ReactNode
    value?: string
    errors: FieldErrors<FormValues>
    placeholder?: string
    options?: {label: string; value: string}[]
    defaultValue?: string | undefined
    isRequired?: boolean
    isReadOnly?: boolean
    isDisabled?: boolean
    minLength?: number
    maxLength?: number
    variant?: string
    size?: string
    id?: string
    onChange?: (e?: string | null) => void
}

const FormFactory = ({
    control,
    type,
    name,
    label = '',
    value,
    errors,
    placeholder,
    options,
    defaultValue,
    isRequired = true,
    isReadOnly = false,
    isDisabled = false,
    minLength,
    maxLength,
    variant,
    size,
    id,
    onChange,
}: FormFactoryProps) => {
    const {formatMessage} = useIntl()
    const emptyFieldErrorLabel = formatMessage(
        {
            id: 'error.field.required',
            defaultMessage: '*Error: field {0} is mandatory',
        },
        {
            0: label,
        },
    )

    const {appPreferences} = useGlobalConfigState()

    const emailRegexFormValidation = new RegExp(appPreferences?.emailRegexFormValidation || /^[\w.%+-]+@[\w.-]+\.[\w]{2,6}$/)

    const ref = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (errors[name] && ref?.current) {
            ref?.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    }, [errors, name])

    const renderInput = (additionalRules?: object) => {
        return (
            <Controller
                control={control}
                name={name}
                // Default to an empty string (never undefined) so the field is controlled from the
                // first render. Without this, useForm() calls with no defaultValues start the input
                // as uncontrolled and React warns when the value later becomes defined.
                defaultValue={defaultValue ?? ''}
                rules={
                    isRequired
                        ? {
                              required: emptyFieldErrorLabel,
                              ...additionalRules,
                          }
                        : additionalRules
                }
                render={({field}) => {
                    switch (type) {
                        case 'textarea':
                            return (
                                <Textarea
                                    {...field}
                                    label={label}
                                    placeholder={placeholder}
                                    ref={ref}
                                    isDisabled={isDisabled}
                                    isRequired={isRequired}
                                    isReadOnly={isReadOnly}
                                    value={value}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    data-intent-redact
                                />
                            )

                        case 'select':
                            return (
                                <Select
                                    {...field}
                                    label={label}
                                    placeholder={placeholder}
                                    ref={ref}
                                    options={options || []}
                                    isDisabled={isDisabled}
                                    isRequired={isRequired}
                                    isReadOnly={isReadOnly}
                                    value={value}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        if (onChange) {
                                            onChange(value)
                                        }
                                    }}
                                    data-intent-redact
                                />
                            )

                        case 'radio':
                            return (
                                <Radio
                                    {...field}
                                    size={size}
                                    ref={ref}
                                    options={options || []}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                >
                                    {label}
                                </Radio>
                            )

                        case 'checkbox':
                            return (
                                <Checkbox
                                    {...field}
                                    size={size}
                                    id={id}
                                    ref={ref}
                                    onChange={(e) => {
                                        field.onChange(e)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                    isInvalid={!!errors[name]}
                                    isRequired={isRequired}
                                    isReadOnly={isReadOnly}
                                    value={value}
                                >
                                    {label}
                                </Checkbox>
                            )

                        default:
                            return (
                                <Input
                                    {...field}
                                    ref={ref}
                                    label={label}
                                    type={type}
                                    placeholder={placeholder}
                                    isDisabled={isDisabled}
                                    variant={variant}
                                    onChange={(e) => {
                                        field.onChange(e.target.value)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    isRequired={isRequired}
                                    isReadOnly={isReadOnly}
                                    // Fall back to the RHF field value so defaultValue / setValue
                                    // (e.g. email prefilled from footer) surface when no explicit
                                    // controlled `value` prop is provided by the caller.
                                    value={value ?? field.value}
                                    maxLength={maxLength}
                                    minLength={minLength}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                    data-intent-redact
                                />
                            )
                    }
                }}
            />
        )
    }

    const componentMap: Record<string, () => JSX.Element> = {
        text: () => renderInput(),
        email: () =>
            renderInput({
                pattern: {
                    value: emailRegexFormValidation,
                    message: `${formatMessage({
                        id: 'label.newsletter.form.email.invalid',
                        defaultMessage: 'Error: enter a valid e-mail address',
                    })}`,
                },
            }),
        tel: () =>
            renderInput({
                pattern: {
                    value: /^(?=(?:[^0-9]*[0-9]){10})[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s0-9]*$/,
                    message: `${formatMessage({
                        id: 'error.message.account.form.phonenotvalid',
                        defaultMessage: 'Error: invalid phone number',
                    })}`,
                },
            }),
        password: () => renderInput(),
        textarea: () => renderInput(),
        select: () => renderInput(),
        radio: () => renderInput(),
        checkbox: () => renderInput(),
        preferenceNewsletterConsent: () =>
            renderInput({
                required: isRequired,
            }),
    }

    const Component = componentMap[type]

    return Component ? Component() : null
}

export {FormFactory, FormValues}
